import { BlobServiceClient } from "@azure/storage-blob";
import {
	useLazyDeleteImageDownloadDataByOrderIdQuery,
	useLazyGetImageDownloadDataByOrderIdQuery,
	useLazyGetImageUploadDataByOrderIdQuery
} from "features/api/imageApi.ts";
import { useCallback } from "react";

// This should be one of three values: tandemdatastoragedev, tandemdatastoragetest, or tandemdatastorageprod
const account = `tandemdatastorage${import.meta.env.VITE_APP_ENV}`;

function useBlobStorage() {
	const [getListToken] = useLazyGetImageDownloadDataByOrderIdQuery();
	const [uploadToken] = useLazyGetImageUploadDataByOrderIdQuery();
	const [getDeleteToken] = useLazyDeleteImageDownloadDataByOrderIdQuery();

	const getContainerBlobs = useCallback(
		async (containerId: string) => {
			const imageUploadData = await getListToken(containerId).unwrap();

			const sas = imageUploadData.credentials;
			const blobServiceClient = new BlobServiceClient(`https://${account}.blob.core.windows.net?${sas}`);
			const containerClient = blobServiceClient.getContainerClient(containerId);

			return containerClient.listBlobsFlat();
		},
		[getListToken]
	);

	const getContainerBlob = useCallback(
		async (containerId: string, blobName: string) => {
			const imageUploadData = await getListToken(containerId).unwrap();

			const sas = imageUploadData.credentials;

			return `https://${account}.blob.core.windows.net/${containerId}/${blobName}?${sas}`;
		},
		[getListToken]
	);

	const uploadContainerBlob = useCallback(
		async (containerId: string, fileName: string, blob: Blob, contentType: string, size: number) => {
			const imageUploadData = await uploadToken(containerId).unwrap();

			const sas = imageUploadData.credentials;
			const blobServiceClient = new BlobServiceClient(`https://${account}.blob.core.windows.net?${sas}`);
			const containerClient = blobServiceClient.getContainerClient(containerId);
			const blockBlobClient = containerClient.getBlockBlobClient(fileName);
			await blockBlobClient.upload(blob, size, {
				blobHTTPHeaders: {
					blobContentType: contentType
				}
			});
			return blockBlobClient.url.split("?")[0];
		},
		[uploadToken]
	);

	const deleteContainerBlob = useCallback(
		async (containerId: string, blobName: string) => {
			const imageUploadData = await getDeleteToken(containerId).unwrap();
			const sas = imageUploadData.credentials;
			const blobServiceClient = new BlobServiceClient(`https://${account}.blob.core.windows.net?${sas}`);
			const containerClient = blobServiceClient.getContainerClient(containerId);

			if (await containerClient.exists()) {
				return containerClient.deleteBlob(blobName);
			}
		},
		[getDeleteToken]
	);
	return { getContainerBlobs, uploadContainerBlob, deleteContainerBlob, getContainerBlob };
}

export default useBlobStorage;
